import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../../../components/ui/dialog';
import type { PassViewRecord } from '../../../utils/fetchWeldViewApis';
import { deriveBinaryPassStatus } from '../../../utils/passViewFormatters';
import type { WpsViolationRecord } from '../../../utils/wpsViolationModel';
import { PassDetailPanel } from './PassDetailPanel';
import { PassSummaryTablePanel, passRowKey } from './PassSummaryTablePanel';

interface PassSummaryDrillDownHostProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  weldNumber: string;
  jobNumber: string;
  passes: PassViewRecord[];
  loading?: boolean;
  statusCalculationMethod?: string;
  outOfLimitContext?: boolean;
  violations?: WpsViolationRecord[];
  limitsConfigured?: boolean;
}

export const PassSummaryDrillDownHost = ({
  open,
  onOpenChange,
  weldNumber,
  jobNumber,
  passes,
  loading = false,
  statusCalculationMethod = 'Instantaneous',
  outOfLimitContext = false,
  violations = [],
  limitsConfigured = true,
}: PassSummaryDrillDownHostProps) => {
  const [selectedPass, setSelectedPass] = useState<PassViewRecord | null>(null);

  useEffect(() => {
    if (!open) {
      setSelectedPass(null);
      return;
    }
    setSelectedPass((prev) => {
      if (!prev) return passes[0] ?? null;
      const stillThere = passes.find((p) => passRowKey(p) === passRowKey(prev));
      return stillThere ?? passes[0] ?? null;
    });
  }, [open, passes]);

  const failedCount = useMemo(
    () => passes.filter((p) => deriveBinaryPassStatus(p) === 'Failed').length,
    [passes],
  );

  const selectedKey = selectedPass ? passRowKey(selectedPass) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[100dvh] w-[100vw] max-h-[100dvh] max-w-[100vw] flex-col gap-3 overflow-hidden rounded-none border-0 p-4 sm:p-5">
        <DialogHeader className="shrink-0">
          <DialogTitle className="font-heading text-blueprint-blue">
            Pass summary
            <span className="ml-2 text-sm font-semibold text-heritage-grey">
              {weldNumber || '—'} · {jobNumber || '—'}
            </span>
            <span className="ml-2 rounded-full bg-[rgba(0,39,63,0.06)] px-2.5 py-0.5 text-sm font-semibold text-heritage-grey">
              {passes.length}
            </span>
          </DialogTitle>
          <DialogDescription className="text-xs text-heritage-grey">
            {loading
              ? 'Loading passes…'
              : `${failedCount} failed · ${passes.length - failedCount} passed — select a pass for limit detail`}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-heritage-grey">Loading pass summary…</p>
        ) : passes.length === 0 ? (
          <p className="text-sm text-heritage-grey">No pass records for this weld.</p>
        ) : (
          <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-5">
            <div className="flex min-h-0 flex-col lg:col-span-2">
              <PassSummaryTablePanel
                passes={passes}
                selectedPassKey={selectedKey}
                onSelectPass={setSelectedPass}
                statusCalculationMethod={statusCalculationMethod}
                outOfLimitContext={outOfLimitContext}
                violations={violations}
              />
            </div>
            <div className="flex min-h-0 flex-col lg:col-span-3">
              <PassDetailPanel
                pass={selectedPass}
                violations={violations}
                limitsConfigured={limitsConfigured}
                statusCalculationMethod={statusCalculationMethod}
              />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
